<template>
  <div class="container">
    <section>
      <div class="layout">
        <div class="child">
          <div class="pure-g">
            <div class="pure-u-1">
              <div>
                <h2>{{ questionTitle }}</h2>
              </div>
            </div>
          </div>
          <div class="pure-g">
            <div class="pure-u-1">
              <div>
                <h3>
                  You have the option to add a media presentation (video, audio, or an image) for
                  participants to view and rate before starting the survey. You can add up to 8
                  rating questions that participants can rate on a 5 point scale. (You must fill in
                  #1 and #5. You have an option of defining the numbers in between but it’s not
                  required.)
                </h3>
              </div>
            </div>
          </div>
          <div>
            <form class="pure-form pure-form-stacked" @submit.prevent="handleSubmit" novalidate>
              <fieldset>
                <div class="pure-g">
                  <div class="pure-u-1-4">
                    <input
                      id="file-input"
                      class="file-input"
                      ref="fileRef"
                      type="file"
                      @change="onChange"
                      hidden
                    />
                    <!-- our custom upload button -->
                    <label for="file-input">Choose File</label>
                  </div>
                  <div class="pure-u-1-4">
                    <!-- name of file chosen -->
                    <div class="file-chosen">{{ fileName }}</div>
                  </div>
                  <div class="pure-u-1-4">
                    <div v-if="uploading && !error" class="centered-row upload-progress">
                      {{ uploadProgress }}%
                    </div>
                    <div class="error" v-if="error">{{ error.message }}</div>
                  </div>
                  <div class="pure-u-1-4">
                    <Loading v-if="uploading && !error" />
                    <div class="trash-icon centered-row" v-else-if="hasFile">
                      <Fai @click="removeFile" :icon="['fal', 'trash-alt']" />
                    </div>
                  </div>
                </div>
                <div
                  v-for="(question, idx) in formModel.preMediaQuestions"
                  :key="idx"
                  class="questions"
                >
                  <div class="pure-g">
                    <div class="pure-u-1">
                      <span class="question-title">Question {{ idx + 1 }}</span>
                      <input type="text" ref="questionsRef" v-model="question.ratingQuestion" />
                      <span class="rating-title">1 means *</span>
                      <input type="text" v-model="question.ratingLow" />
                      <div
                        style="color: red"
                        v-show="
                          !question.ratingLow &&
                          question.ratingQuestion &&
                          !isFormModelValid &&
                          pressedSubmit
                        "
                      >
                        *Required Field
                      </div>
                      <span class="rating-title">2 means</span>
                      <input type="text" v-model="question.ratingLowMid" />
                      <span class="rating-title">3 means</span>
                      <input type="text" v-model="question.ratingMid" />
                      <span class="rating-title">4 means</span>
                      <input type="text" v-model="question.ratingHighMid" />
                      <span class="rating-title">5 means *</span>
                      <input type="text" v-model="question.ratingHigh" />
                      <div
                        style="color: red"
                        v-show="
                          !question.ratingHigh &&
                          question.ratingQuestion &&
                          !isFormModelValid &&
                          pressedSubmit
                        "
                      >
                        *Required Field
                      </div>
                    </div>
                  </div>
                </div>
                <div class="pure-g">
                  <div class="pure-u-1-2">
                    <Fai
                      v-if="canAddQuestion"
                      class="add-button"
                      :icon="['far', 'plus-circle']"
                      @click="addQuestion"
                    />
                  </div>
                  <div class="pure-u-1-2">
                    <Fai
                      v-if="canRemoveQuestion"
                      class="minus-button"
                      :icon="['far', 'minus-circle']"
                      @click="removeQuestion"
                    />
                  </div>
                </div>
                <button type="submit" class="hidden-submit"></button>
              </fieldset>
              <BigStudyNavigation></BigStudyNavigation>
            </form>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import {
  GET_NEXT_STUDY_STEP_ROUTE,
  GET_BACKEND_URL,
  GET_IN_PROGRESS_PRESENTATION_MEDIA,
  SET_IN_PROGRESS_PRESENTATION_MEDIA,
  UPDATE_STUDY,
} from "../../services/constants";
import http from "../../services/axios";
import Loading from "../views/Loading.vue";

const NO_FILE = "No file chosen";

export default {
  name: "PresentationMedia",
  data() {
    return {
      formModel: {},
      presignedData: null,
      uploading: false,
      fileName: NO_FILE,
      uploadProgress: 0,
      error: null,
      pressedSubmit: false,
    };
  },
  computed: {
    ...mapGetters([GET_NEXT_STUDY_STEP_ROUTE, GET_BACKEND_URL, GET_IN_PROGRESS_PRESENTATION_MEDIA]),
    questionTitle() {
      return "Pre-Presentation";
    },
    isFormModelValid() {
      return this.formModel.preMediaQuestions.every((question) => {
        if (!question.ratingQuestion) {
          return true;
        }
        return question.ratingQuestion && question.ratingLow && question.ratingHigh;
      });
    },
    canAddQuestion() {
      return this.formModel.preMediaQuestions.length < 8;
    },
    canRemoveQuestion() {
      return this.formModel.preMediaQuestions.length > 1;
    },
    prePresentationPayload() {
      return {
        prePresentation: this.formModel.prePresentation ? this.formModel.prePresentation : null,
        preMediaQuestions: this.formModel.prePresentation ? this.formModel.preMediaQuestions : null,
      };
    },
    savedState() {
      return this[GET_IN_PROGRESS_PRESENTATION_MEDIA];
    },
    hasSavedState() {
      return this.savedState.prePresentation;
    },
    hasFile() {
      return !!this.formModel.prePresentation || this.fileName !== NO_FILE;
    },
  },
  methods: {
    handleSubmit() {
      this.pressedSubmit = true;
      if (this.isFormModelValid) {
        if (this.uploading) {
          this.formModel.postPresentation = null;
        }
        this.formModel.preMediaQuestions.forEach((questions) => {
          for (let key in questions) {
            if (
              key === "ratingQuestion" ||
              key === "ratingLow" ||
              key === "ratingLowMid" ||
              key === "ratingMid" ||
              key === "ratingHighMid" ||
              key === "ratingHigh"
            ) {
              questions[key] = questions[key].replace(/,/g, "&comma;");
            }
          }
        });
        this.formModel.preMediaQuestions.forEach((question) => {
          question.ratingIntermediate = question.ratingLowMid + "," + question.ratingMid + "," + question.ratingHighMid;
        });
        this.$store.commit(SET_IN_PROGRESS_PRESENTATION_MEDIA, this.formModel);
        this.$store.dispatch(UPDATE_STUDY).then(() => {
          this.$router.push(this[GET_NEXT_STUDY_STEP_ROUTE]);
        });
      }
    },
    async onChange() {
      this.uploading = true;
      const file = this.$refs.fileRef.files[0];
      this.fileName = file.name;
      if (file) {
        await this.getPresignedUrl(file);
        await this.uploadFile(file);
      }
      this.uploadProgress = 0;
      this.uploading = false;
    },
    async getPresignedUrl(file) {
      const payload = {
        filename: file.name,
        contentType: file.type,
        size: file.size,
      };
      const url = this[GET_BACKEND_URL]("storage/blobs/upload");
      await http
        .post(url, payload)
        .then((response) => {
          this.presignedData = response.data;
          this.formModel.prePresentation = this.presignedData.key;
        })
        .catch((error) => {
          this.error = error;
        });
    },
    async uploadFile(file) {
      const url = this.presignedData.url;
      const httpConfig = {
        headers: {
          "Content-Type": file.type,
        },
        onUploadProgress: (progressEvent) => {
          this.uploadProgress =
            Math.round((progressEvent.loaded / progressEvent.total) * 1000) / 10;
        },
      };
      await http.put(url, file, httpConfig).catch((error) => {
        this.error = error;
      });
    },
    initFormModel() {
      this.formModel = {
        preMediaQuestions: [
          {
            id: 1,
            ratingQuestion: "",
            ratingLow: "",
            ratingLowMid: "",
            ratingMid: "",
            ratingHighMid: "",
            ratingHigh: "",
          },
        ],
      };
    },
    addQuestion() {
      this.formModel.preMediaQuestions.push({
        id: this.formModel.preMediaQuestions.length + 1,
        ratingQuestion: "",
        ratingLow: "",
        ratingLowMid: "",
        ratingMid: "",
        ratingHighMid: "",
        ratingHigh: "",
      });
    },
    loadSavedState() {
      this.formModel = this.savedState;
    },
    removeQuestion() {
      this.formModel.preMediaQuestions.pop();
    },
    removeFile() {
      this.formModel.prePresentation = null;
      this.fileName = NO_FILE;
    },
    getFileFromUrl(url) {
      return http.get(url);
    },
  },
  async created() {
    if (this.hasSavedState) {
      this.loadSavedState();
      if (this.formModel.prePresentation) {
        const fileUrl = this[GET_BACKEND_URL](
          `storage/blobs/${this.formModel.prePresentation}/data`
        );
        const response = await this.getFileFromUrl(fileUrl);
        this.fileName = response.data.filename;
      }
      this.formModel.preMediaQuestions.forEach((question) => {
        if (question.ratingIntermediate.length > 2) {
          // if it has 2 commas it is still empty
          const ratingIntermediate = question.ratingIntermediate.split(",");
          if (ratingIntermediate.length > 2) {
            question.ratingLowMid = ratingIntermediate[0];
            question.ratingMid = ratingIntermediate[1];
            question.ratingHighMid = ratingIntermediate[2];
          }
        }
      });
    } else {
      this.initFormModel();
    }
  },
  components: { Loading },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
@import "../../scss/main";

.container {
  height: 100%;
}

section {
  height: 100%;
}

.layout {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100%;
}

.child {
  width: 100%;
  max-width: 500px;
  margin: 1em;
}

h2 {
  text-align: center;
  color: $red-2;
}

h3 {
  color: $cyan-blue-3;
}

.pure-form input[type="text"] {
  box-shadow: none;
  width: 100%;
  color: $cyan-blue-3;
  border-color: $cyan-blue-3;

  &:focus {
    border-color: $cyan-blue-1;
  }
  &:placeholder-shown {
    color: $gray-3;
  }
  &:-ms-input-placeholder {
    color: $gray-3;
  }
}

.question-title {
  font-size: 1em;
  font-weight: bold;
  color: $cyan-blue-4;
}

.rating-title {
  font-size: 0.83em;
  font-weight: bold;
  color: $cyan-blue-3;
}

::placeholder {
  color: $gray-3;
  opacity: 1;
}

.add-button {
  color: $red-2;
  font-size: 200%;
  padding-left: 1em;
  cursor: pointer;
}
.minus-button {
  color: $red-2;
  font-size: 200%;
  padding-right: 1em;
  cursor: pointer;
  float: right;
}

label {
  background-color: $red-2;
  color: white;
  padding: 0.5rem;
  font-family: sans-serif;
  border-radius: 0.3rem;
  cursor: pointer;
  margin-top: 1rem;
}

.file-chosen {
  color: $cyan-blue-3;
  font-size: 0.8em;
  font-weight: bold;
  margin-top: 1rem;
  padding: 0.5rem;
}
.trash-icon {
  color: $red-2;
  font-size: 150%;
  margin-top: 1rem;
  cursor: pointer;
}

.questions {
  margin-top: 1.5rem;
}

.upload-progress {
  margin-top: 1rem;
  color: $red-2;
  font-size: 150%;
}
.error {
  color: $red-2;
  margin-top: 1rem;
  padding: 0.5rem;
}
</style>
